import { useCallback, useEffect, useState } from 'react';
import type { AdminListResponse } from '../../../domain/superadmin/types';
import type { SupportTicketDetail, SupportTicketRow } from '../../../domain/support/types';
import {
  createSupportTicket,
  fetchSupportTicket,
  fetchSupportTickets,
  replySupportTicket,
} from '../../../services/modules/support/supportApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';

const PAGE_SIZE = 25;

export function useSupportTickets(organizationId: string | null | undefined) {
  const [data, setData] = useState<AdminListResponse<SupportTicketRow> | null>(null);
  const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((e: unknown, fallback: string) => {
    setError(e instanceof Error ? e.message : fallback);
  }, []);

  const load = useCallback(async () => {
    if (!organizationId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchSupportTickets(organizationId, {
        page,
        pageSize: PAGE_SIZE,
        status: status || undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setError('Acesso não autorizado para gerenciar tickets desta organização.');
        return;
      }
      handleError(e, 'Erro ao carregar tickets de suporte');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, page, status, handleError]);

  const loadDetail = useCallback(
    async (ticketId: string) => {
      if (!organizationId) return;
      setIsDetailLoading(true);
      setError(null);
      try {
        const result = await fetchSupportTicket(organizationId, ticketId);
        setDetail(result);
      } catch (e) {
        if (e instanceof SuperadminApiError && e.isForbidden) {
          setError('Acesso não autorizado para visualizar este ticket.');
          return;
        }
        handleError(e, 'Erro ao carregar ticket');
      } finally {
        setIsDetailLoading(false);
      }
    },
    [organizationId, handleError]
  );

  const createTicket = useCallback(
    async (subject: string, body: string) => {
      if (!organizationId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const created = await createSupportTicket(organizationId, subject, body);
        setDetail(created);
        await load();
        return created;
      } catch (e) {
        if (e instanceof SuperadminApiError && e.isForbidden) {
          setError('Acesso não autorizado para criar tickets.');
          return null;
        }
        handleError(e, 'Erro ao criar ticket');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [organizationId, load, handleError]
  );

  const reply = useCallback(
    async (ticketId: string, body: string) => {
      if (!organizationId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const updated = await replySupportTicket(organizationId, ticketId, body);
        setDetail(updated);
        await load();
        return updated;
      } catch (e) {
        if (e instanceof SuperadminApiError && e.isForbidden) {
          setError('Acesso não autorizado para responder este ticket.');
          return null;
        }
        handleError(e, 'Erro ao responder ticket');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [organizationId, load, handleError]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    setDetail(null);
  }, [organizationId]);

  return {
    data,
    detail,
    isLoading,
    isDetailLoading,
    isSaving,
    error,
    refetch: load,
    loadDetail,
    createTicket,
    reply,
    page,
    setPage,
    status,
    setStatus,
    pageSize: PAGE_SIZE,
  };
}
